import React, { useEffect, useMemo, useRef } from 'react';
import { Compartment, EditorState, RangeSetBuilder } from '@codemirror/state';
import {
  Decoration,
  EditorView,
  MatchDecorator,
  ViewPlugin,
  placeholder,
} from '@codemirror/view';

const TAG_REGEX = /<[^<>\n]*>/g;

const escapeRegExp = (value) => (
  String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
);

const toTagMatcher = (pattern) => {
  if (!pattern) return null;

  if (pattern instanceof RegExp) {
    return (tag) => {
      pattern.lastIndex = 0;
      return pattern.test(tag);
    };
  }

  if (typeof pattern === 'string') {
    if (!pattern.includes('*')) {
      const normalizedPattern = pattern.toLowerCase();
      return (tag) => tag.toLowerCase() === normalizedPattern;
    }

    const wildcardPattern = escapeRegExp(pattern).replace(/\\\*/g, '[^<>\\n]*');
    const compiled = new RegExp(`^${wildcardPattern}$`, 'i');
    return (tag) => compiled.test(tag);
  }

  return null;
};

const buildSet = (items) => (
  new Set((items || []).map((item) => String(item || '').toLowerCase()).filter(Boolean))
);

const classifyTagFactory = ({ knownTags = [], knownTagPatterns = [], requiredTags = [] }) => {
  const knownTagSet = buildSet(knownTags);
  const requiredTagSet = buildSet(requiredTags);
  const knownTagMatchers = (knownTagPatterns || []).map(toTagMatcher).filter(Boolean);

  return (tag) => {
    const normalizedTag = String(tag || '').toLowerCase();

    if (requiredTagSet.has(normalizedTag)) return 'required';
    if (knownTagSet.has(normalizedTag)) return 'known';
    if (knownTagMatchers.some((matcher) => matcher(tag))) return 'known';
    return 'unknown';
  };
};

const buildTagHighlightExtension = (classifyTag) => {
  const tagDecorator = new MatchDecorator({
    regexp: TAG_REGEX,
    decoration: (match) => {
      const tagClass = classifyTag(match[0]);
      if (tagClass === 'required') {
        return Decoration.mark({ class: 'cm-prompt-tag-required' });
      }
      if (tagClass === 'known') {
        return Decoration.mark({ class: 'cm-prompt-tag-known' });
      }
      return Decoration.mark({ class: 'cm-prompt-tag-unknown' });
    },
  });

  return ViewPlugin.fromClass(class {
    constructor(view) {
      this.decorations = tagDecorator.createDeco(view);
    }

    update(update) {
      this.decorations = tagDecorator.updateDeco(update, this.decorations);
    }
  }, {
    decorations: (instance) => instance.decorations,
  });
};

const findJsonObjectBlocks = (text) => {
  const blocks = [];
  let depth = 0;
  let start = -1;
  let inString = false;
  let escaped = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];

    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (char === '\\') {
        escaped = true;
      } else if (char === '"') {
        inString = false;
      }
      continue;
    }

    if (char === '"') {
      inString = true;
      escaped = false;
      continue;
    }

    if (char === '{') {
      if (depth === 0) start = index;
      depth += 1;
      continue;
    }

    if (char === '}' && depth > 0) {
      depth -= 1;
      if (depth === 0 && start >= 0) {
        blocks.push({ from: start, to: index + 1 });
        start = -1;
      }
    }
  }

  return blocks;
};

const JSON_NUMBER_PATTERN = /^-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?/;

const isBoundaryChar = (char) => !char || !/[A-Za-z0-9_$]/.test(char);

const buildJsonHighlightExtension = () => {
  const marks = {
    key: Decoration.mark({ class: 'cm-json-key' }),
    string: Decoration.mark({ class: 'cm-json-string' }),
    number: Decoration.mark({ class: 'cm-json-number' }),
    boolean: Decoration.mark({ class: 'cm-json-boolean' }),
    null: Decoration.mark({ class: 'cm-json-null' }),
    punctuation: Decoration.mark({ class: 'cm-json-punctuation' }),
  };

  const buildDecorations = (view) => {
    const builder = new RangeSetBuilder();
    const text = view.state.doc.toString();
    const jsonBlocks = findJsonObjectBlocks(text);

    jsonBlocks.forEach((block) => {
      const blockText = text.slice(block.from, block.to);
      let index = 0;

      while (index < blockText.length) {
        const char = blockText[index];

        if (/\s/.test(char)) {
          index += 1;
          continue;
        }

        if (char === '"') {
          let cursor = index + 1;
          let escaped = false;

          while (cursor < blockText.length) {
            const current = blockText[cursor];
            if (escaped) {
              escaped = false;
            } else if (current === '\\') {
              escaped = true;
            } else if (current === '"') {
              break;
            }
            cursor += 1;
          }

          const end = Math.min(cursor + 1, blockText.length);
          let probe = end;
          while (probe < blockText.length && /\s/.test(blockText[probe])) {
            probe += 1;
          }
          const isKey = blockText[probe] === ':';
          builder.add(
            block.from + index,
            block.from + end,
            isKey ? marks.key : marks.string
          );
          index = end;
          continue;
        }

        if (/[{}[\]:,]/.test(char)) {
          builder.add(block.from + index, block.from + index + 1, marks.punctuation);
          index += 1;
          continue;
        }

        const numberMatch = blockText.slice(index).match(JSON_NUMBER_PATTERN);
        if (numberMatch) {
          const value = numberMatch[0];
          builder.add(block.from + index, block.from + index + value.length, marks.number);
          index += value.length;
          continue;
        }

        const nextChars = blockText.slice(index, index + 5);
        if (
          nextChars.startsWith('true')
          && isBoundaryChar(blockText[index - 1])
          && isBoundaryChar(blockText[index + 4])
        ) {
          builder.add(block.from + index, block.from + index + 4, marks.boolean);
          index += 4;
          continue;
        }
        if (
          nextChars.startsWith('false')
          && isBoundaryChar(blockText[index - 1])
          && isBoundaryChar(blockText[index + 5])
        ) {
          builder.add(block.from + index, block.from + index + 5, marks.boolean);
          index += 5;
          continue;
        }
        if (
          nextChars.startsWith('null')
          && isBoundaryChar(blockText[index - 1])
          && isBoundaryChar(blockText[index + 4])
        ) {
          builder.add(block.from + index, block.from + index + 4, marks.null);
          index += 4;
          continue;
        }

        index += 1;
      }
    });

    return builder.finish();
  };

  return ViewPlugin.fromClass(class {
    constructor(view) {
      this.decorations = buildDecorations(view);
    }

    update(update) {
      if (update.docChanged || update.viewportChanged) {
        this.decorations = buildDecorations(update.view);
      }
    }
  }, {
    decorations: (instance) => instance.decorations,
  });
};

const buildTheme = (tone) => {
  const isSlateTone = tone === 'slate';

  return EditorView.theme({
    '&': {
      height: '100%',
      backgroundColor: 'transparent',
    },
    '.cm-scroller': {
      overflow: 'auto',
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
      fontSize: '0.75rem',
      lineHeight: '1.25rem',
    },
    '.cm-content': {
      minHeight: '100%',
      padding: '0.5rem 0.75rem',
      color: isSlateTone ? '#1e293b' : '#1f2937',
      caretColor: isSlateTone ? '#1e293b' : '#1f2937',
    },
    '.cm-line': {
      padding: '0',
    },
    '&.cm-focused': {
      outline: 'none',
    },
    '.cm-placeholder': {
      color: isSlateTone ? '#94a3b8' : '#9ca3af',
    },
    '.cm-selectionBackground': {
      backgroundColor: '#bfdbfe !important',
    },
    '.cm-content ::selection': {
      backgroundColor: '#bfdbfe',
    },
    '.cm-prompt-tag-required': {
      borderRadius: '0.125rem',
      backgroundColor: 'rgba(219, 234, 254, 0.95)',
      color: '#1d4ed8',
    },
    '.cm-prompt-tag-known': {
      borderRadius: '0.125rem',
      backgroundColor: 'rgba(209, 250, 229, 0.9)',
      color: '#047857',
    },
    '.cm-prompt-tag-unknown': {
      borderRadius: '0.125rem',
      backgroundColor: 'rgba(254, 243, 199, 0.95)',
      color: '#b45309',
    },
    '.cm-json-key': {
      color: '#0369a1',
      fontWeight: '600',
    },
    '.cm-json-string': {
      color: '#047857',
    },
    '.cm-json-number': {
      color: '#b45309',
    },
    '.cm-json-boolean': {
      color: '#be123c',
      fontWeight: '600',
    },
    '.cm-json-null': {
      color: '#475569',
      fontStyle: 'italic',
    },
    '.cm-json-punctuation': {
      color: '#64748b',
    },
  });
};

const PromptTagEditor = ({
  id,
  value,
  onChange,
  disabled = false,
  readOnly = false,
  placeholder: placeholderText = '',
  ariaLabel,
  knownTags = [],
  knownTagPatterns = [],
  requiredTags = [],
  minHeightClassName = 'min-h-[380px]',
  tone = 'gray',
}) => {
  const hostRef = useRef(null);
  const viewRef = useRef(null);
  const suppressChangeRef = useRef(false);
  const onChangeRef = useRef(onChange);
  const editableCompartmentRef = useRef(new Compartment());
  const tagHighlightCompartmentRef = useRef(new Compartment());
  const themeCompartmentRef = useRef(new Compartment());
  const placeholderCompartmentRef = useRef(new Compartment());
  const contentAttributesCompartmentRef = useRef(new Compartment());
  const initialSetupRef = useRef(null);

  onChangeRef.current = onChange;

  const normalizedValue = String(value || '');

  const classifyTag = useMemo(
    () => classifyTagFactory({ knownTags, knownTagPatterns, requiredTags }),
    [knownTagPatterns, knownTags, requiredTags]
  );

  const toneTheme = useMemo(() => buildTheme(tone), [tone]);
  const jsonHighlightExtension = useMemo(() => buildJsonHighlightExtension(), []);

  const editorModeExtensions = useMemo(() => {
    const isReadOnly = readOnly || disabled;
    return [
      EditorState.readOnly.of(isReadOnly),
      EditorView.editable.of(!isReadOnly),
    ];
  }, [disabled, readOnly]);

  const placeholderExtension = useMemo(
    () => placeholder(placeholderText || ''),
    [placeholderText]
  );

  const contentAttributesExtension = useMemo(() => {
    const attributes = {
      spellcheck: 'false',
      autocorrect: 'off',
      autocapitalize: 'off',
      'data-gramm': 'false',
    };
    if (ariaLabel) {
      attributes['aria-label'] = ariaLabel;
    }
    if (id) {
      attributes.id = id;
    }
    return EditorView.contentAttributes.of(attributes);
  }, [ariaLabel, id]);

  if (!initialSetupRef.current) {
    initialSetupRef.current = {
      doc: normalizedValue,
      editorModeExtensions,
      tagHighlightExtension: buildTagHighlightExtension(classifyTag),
      toneTheme,
      placeholderExtension,
      contentAttributesExtension,
    };
  }

  useEffect(() => {
    if (!hostRef.current || viewRef.current) return undefined;

    const updateListener = EditorView.updateListener.of((update) => {
      if (!update.docChanged || suppressChangeRef.current) return;
      onChangeRef.current?.(update.state.doc.toString());
    });

    const view = new EditorView({
      state: EditorState.create({
        doc: initialSetupRef.current.doc,
        extensions: [
          EditorView.lineWrapping,
          updateListener,
          editableCompartmentRef.current.of(initialSetupRef.current.editorModeExtensions),
          jsonHighlightExtension,
          tagHighlightCompartmentRef.current.of(initialSetupRef.current.tagHighlightExtension),
          themeCompartmentRef.current.of(initialSetupRef.current.toneTheme),
          placeholderCompartmentRef.current.of(initialSetupRef.current.placeholderExtension),
          contentAttributesCompartmentRef.current.of(initialSetupRef.current.contentAttributesExtension),
        ],
      }),
      parent: hostRef.current,
    });

    viewRef.current = view;

    return () => {
      view.destroy();
      viewRef.current = null;
    };
  }, [jsonHighlightExtension]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;

    view.dispatch({
      effects: editableCompartmentRef.current.reconfigure(editorModeExtensions),
    });
  }, [editorModeExtensions]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;

    view.dispatch({
      effects: placeholderCompartmentRef.current.reconfigure(placeholderExtension),
    });
  }, [placeholderExtension]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;

    view.dispatch({
      effects: contentAttributesCompartmentRef.current.reconfigure(contentAttributesExtension),
    });
  }, [contentAttributesExtension]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;

    view.dispatch({
      effects: tagHighlightCompartmentRef.current.reconfigure(buildTagHighlightExtension(classifyTag)),
    });
  }, [classifyTag]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;

    view.dispatch({
      effects: themeCompartmentRef.current.reconfigure(toneTheme),
    });
  }, [toneTheme]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;

    const current = view.state.doc.toString();
    if (current === normalizedValue) return;

    suppressChangeRef.current = true;
    view.dispatch({
      changes: { from: 0, to: current.length, insert: normalizedValue },
    });
    suppressChangeRef.current = false;
  }, [normalizedValue]);

  const toneStyles = tone === 'slate'
    ? 'border-slate-200 bg-slate-50 focus-within:ring-blue-500'
    : 'border-gray-200 bg-gray-50 focus-within:ring-blue-500';

  return (
    <div
      className={`relative overflow-hidden rounded-lg border transition focus-within:ring-2 ${toneStyles} ${
        disabled ? 'opacity-80' : ''
      } ${minHeightClassName} resize-y`}
    >
      <div ref={hostRef} className="h-full w-full" />
    </div>
  );
};

export default PromptTagEditor;
